/**
 * Email Verification Page for v0.8.0
 * Luxury glass morphism design
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { sendVerification, verifyEmail } from '../../services/emailApi';
import './EmailVerificationPage.css';

const EmailVerificationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  // Countdown timer for resend
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  const handleCodeChange = (index: number, value: string) => {
    if (value.length <= 1) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.querySelector(`input[data-index="${index + 1}"]`) as HTMLInputElement;
        nextInput?.focus();
      }

      // Auto-submit when all 6 digits are entered
      if (index === 5 && value) {
        handleVerify();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');

    // Extract only digits from pasted content
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);

    if (digits.length > 0) {
      const newCode = [...code];

      // Fill in the pasted digits
      for (let i = 0; i < digits.length; i++) {
        newCode[i] = digits[i];
      }

      setCode(newCode);

      // Focus the next empty input or the last filled one
      const nextFocusIndex = Math.min(digits.length, 5);
      const nextInput = document.querySelector(`input[data-index="${nextFocusIndex}"]`) as HTMLInputElement;
      nextInput?.focus();

      // Auto-submit if all 6 digits are pasted
      if (digits.length === 6) {
        setTimeout(() => handleVerify(), 100);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index]) {
      // Focus previous input
      const prevInput = document.querySelector(`input[data-index="${index - 1}"]`) as HTMLInputElement;
      prevInput?.focus();
    }
  };

  const handleVerify = async () => {
    const codeValue = code.join('');
    if (codeValue.length !== 6) {
      setError('請輸入完整的 6 位數驗證碼');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await verifyEmail(email, codeValue);
      if (response.success && response.data?.verified) {
        setVerified(true);
        // Redirect to dashboard after 2 seconds
        setTimeout(() => navigate('/member'), 2000);
      } else {
        setError('驗證碼錯誤或已過期');
      }
    } catch (err) {
      setError((err as Error)?.message || '驗證失敗，請重試');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (timeLeft > 0) return;

    setResendLoading(true);
    setError('');

    try {
      const response = await sendVerification();
      if (response.success) {
        setResendSuccess(true);
        setTimeLeft(60); // 60 seconds cooldown
        setTimeout(() => setResendSuccess(false), 5000);
      } else {
        setError(response.error || '發送失敗，請稍後再試');
      }
    } finally {
      setResendLoading(false);
    }
  };

  if (verified) {
    return (
      <div className="email-verification-page">
        <div className="email-verification-page__container email-verification-page__container--success">
          <div className="email-verification-page__icon">✓</div>
          <h1 className="email-verification-page__title">Email 驗證成功！</h1>
          <p className="email-verification-page__message">正在跳轉到會員中心...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="email-verification-page">
      <div className="email-verification-page__container">
        <div className="email-verification-page__logo">
          🚗
        </div>
        <h1 className="email-verification-page__title">溫柔小姊姊｜選物 x 微批發</h1>

        <p className="email-verification-page__subtitle">
          我們已發送 6 位數驗證碼到
          <br />
          <strong>{email}</strong>
        </p>

        {/* Code Input */}
        <div className="verification-code-input">
          {code.map((digit, index) => (
            <input
              key={index}
              type="text"
              inputMode="numeric"
              maxLength={6}
              data-index={index}
              value={digit}
              onChange={(e) => handleCodeChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={(e) => handlePaste(e)}
              className="verification-code-input__digit"
              disabled={loading}
              autoFocus={index === 0}
            />
          ))}
        </div>

        {error && (
          <p className="email-verification-page__error">{error}</p>
        )}

        {/* Resend Button */}
        <div className="email-verification-page__resend">
          {resendSuccess ? (
            <p className="email-verification-page__success">驗證碼已重新發送！</p>
          ) : timeLeft > 0 ? (
            <p className="email-verification-page__countdown">
              {timeLeft} 秒後可重新發送
            </p>
          ) : (
            <button
              onClick={handleResend}
              disabled={resendLoading}
              className="email-verification-page__resend-btn"
            >
              {resendLoading ? '發送中...' : '重新發送驗證碼'}
            </button>
          )}
        </div>

        {/* Info */}
        <div className="email-verification-page__info">
          <p>• 驗證碼將於 30 分鐘後過期</p>
          <p>• 請檢查您的收件夾或垃圾郵件</p>
        </div>

        {/* Skip for now */}
        <button
          onClick={() => navigate('/member')}
          className="email-verification-page__skip-btn"
        >
          稍後驗證，前往會員中心
        </button>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
